import { cn } from '@/lib/cn';
import {
  NEAR_THRESHOLD,
  deriveDarkPair,
  nearestColorToken,
  themeSurfaces,
  toHex,
  toOklch,
} from '@personalizehq/editor';
import type { Theme } from '@personalizehq/schemas/ir';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Plus } from 'lucide-react';
import { useState } from 'react';

// MINTING A COLOUR, IN ONE GESTURE.
//
// Raw colour is forbidden on the wire — a bare `#FF0000` has no dark variant and dark mode would rot
// (SPEC §4) — and that constraint is worth keeping: it is why the SDK needs no variant-resolution
// machinery at all, where Plasmic threads a `VariantedStylesHelper` through every control to do the
// same job in the client.
//
// But SPEC promised a compensation ("the editor mints tokens") that was never built, so the author
// paid the whole cost: six steps through the Theme panel to use one new colour. This is the
// compensation. The pair still exists, still resolves, still publishes — the author just stops
// hand-managing it.
//
// IT ASKS, WITH THE ANSWER ALREADY FILLED IN. Deriving silently would be worse: the derivation is
// right for most colours and a wrong value nobody was asked about is harder to notice than a missing
// one. Both halves are shown, both editable, and the reason is stated.

const SWATCH = 'size-6 shrink-0 rounded border';

export function ColorMintPopover({
  theme,
  onMint,
}: {
  theme: Theme;
  /** Mint AND assign, in one `applyChange`. See `Setter` — ⌘Z must undo "I used a new colour",
   *  never leave an orphan token behind. */
  onMint: (light: string, dark: string) => void;
}): React.JSX.Element {
  const [hostOpen, setHostOpen] = useState(false);
  return (
    <PopoverPrimitive.Root open={hostOpen} onOpenChange={setHostOpen}>
      {/* `[data-popover]` so the inventory walker opens it and drives what is inside — a control the
          walker cannot reach is a control a screen-reader user cannot reach either. */}
      <PopoverPrimitive.Trigger
        data-popover=""
        aria-label="new colour"
        title="New colour"
        className="grid size-8 shrink-0 place-items-center rounded-lg border border-transparent bg-muted/70 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Plus className="size-3.5" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={6}
          align="end"
          className="z-50 w-[236px] rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
        >
          <ColorMintForm theme={theme} onMint={onMint} onDone={() => setHostOpen(false)} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** THE FORM, WITHOUT THE POPOVER THAT USUALLY HOSTS IT.
 *
 *  Split for the same reason as `TokenSwatchGrid`: a Radix popover cannot be nested inside another
 *  one — the inner content is portalled to the body, so the outer sees the interaction as outside
 *  itself and dismisses it. The background row edits its colour from inside its own popover and needs
 *  the mint form THERE, not as a second layer on top of it. */
export function ColorMintForm({
  theme,
  onMint,
  onDone,
}: {
  theme: Theme;
  onMint: (light: string, dark: string) => void;
  onDone: () => void;
}): React.JSX.Element {
  const [light, setLight] = useState('#4f46e5');
  // `null` means "follow the derivation"; a string means the author took it over. Tracking it this
  // way keeps the dark half live while they are still moving the light one, and stops updating the
  // moment they disagree with us.
  const [darkOverride, setDarkOverride] = useState<string | null>(null);

  const surfaces = themeSurfaces(theme);
  const dark = darkOverride ?? deriveDarkPair(light, surfaces);
  const near = nearestColorToken(theme, light);
  const isNear = near !== null && near.distance < NEAR_THRESHOLD;

  const reset = (): void => {
    setDarkOverride(null);
    onDone();
  };

  return (
    <div className="space-y-2">
        <div className="flex items-center gap-2">
          {/* The native picker, exactly as the Theme panel already uses. It is a real colour wheel
              on every platform for free, and it is the one control here that cannot be got wrong. */}
          <input
            type="color"
            aria-label="new colour value"
            value={light}
            onChange={(event) => setLight(event.target.value)}
            className="size-7 shrink-0 bg-transparent"
          />
          <input
            aria-label="new colour hex"
            value={light}
            onChange={(event) => {
              const next = event.target.value;
              setLight(next);
            }}
            // Normalised on blur, not on every keystroke: re-writing the field mid-type fights the
            // author's cursor, and a half-typed hex is not an error yet.
            onBlur={() => setLight(toHex(toOklch(light)))}
            className="h-7 min-w-0 flex-1 rounded-md border border-transparent bg-muted/70 px-2 font-mono text-[11px] transition-colors focus-visible:border-ring focus-visible:bg-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>

        {/* BOTH HALVES, ALWAYS VISIBLE. The pair is what the token IS (SPEC §4), so hiding the dark
            one behind a disclosure would be hiding half the value the author is committing to. */}
        <div className="space-y-1">
          <Row label="light" value={light} onChange={setLight} />
          <Row label="dark" value={dark} onChange={setDarkOverride} />
          <p className="pl-[46px] text-[10px] leading-snug text-muted-foreground">
            {darkOverride === null ? darkNote(light, dark) : 'set by hand'}
          </p>
        </div>

        {/* THE ANTI-SPRAWL OFFER. Minting on demand is what makes nine nearly-identical greys
            possible; this is what stops it, and it costs one comparison over the theme. */}
        {isNear ? (
          <button
            type="button"
            // Named, not just visually labelled: the text is split across three elements so a
            // screen reader (and the test) would otherwise have to reassemble it.
            aria-label={`use ${near.token} instead`}
            onClick={() => {
              const pair = theme.colors[near.token];
              if (pair) onMint(pair.light, pair.dark);
              reset();
            }}
            className="flex w-full items-center gap-1.5 rounded-md bg-muted/70 px-2 py-1.5 text-left text-[11px] transition-colors hover:bg-muted"
          >
            <span className={cn(SWATCH, 'size-4 overflow-hidden')} aria-hidden>
              <span className="flex size-full">
                <span className="w-1/2" style={{ background: theme.colors[near.token]?.light }} />
                <span className="w-1/2" style={{ background: theme.colors[near.token]?.dark }} />
              </span>
            </span>
            <span className="min-w-0 flex-1 truncate">
              Close to <span className="font-medium text-foreground">{near.token}</span> — use it
            </span>
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => {
            onMint(light, dark);
            reset();
          }}
          className="h-7 w-full rounded-md bg-primary text-[11px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Add colour
        </button>
    </div>
  );
}

/** One half of the pair: swatch, name, editable hex. */
function Row({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label className="flex items-center gap-2">
      <span className="w-[38px] shrink-0 text-[11px] text-muted-foreground">{label}</span>
      <span className={SWATCH} style={{ background: value }} aria-hidden />
      <input
        aria-label={`new colour ${label}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => onChange(toHex(toOklch(value)))}
        className="h-6 min-w-0 flex-1 rounded border border-transparent bg-muted/70 px-1.5 font-mono text-[11px] transition-colors focus-visible:border-ring focus-visible:bg-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
    </label>
  );
}

/** SAY WHY. The derivation preserves contrast against the surface, so for a near-black it inverts and
 *  for a mid-tone brand colour it lightens — two visibly different outcomes from one rule. Naming
 *  which one happened is what makes the proposal reviewable instead of magic. */
function darkNote(light: string, dark: string): string {
  const from = toOklch(light).l;
  const to = toOklch(dark).l;
  if (Math.abs(to - from) > 0.45) return 'inverted for dark surfaces';
  return to > from ? 'lightened for dark surfaces' : 'darkened for dark surfaces';
}
